/**
 * The long-shift flag on a clock timesheet, as api#992 writes it.
 *
 * `timesheets.anomaly_flags` is `text[]`, one element per flag, `code: reason`:
 *
 *     long_shift: 30.15h on the clock, over the 16h ceiling — check the clock-out time
 *
 * The API writes, rewrites and clears it whenever `raw_minutes` moves. This
 * module only READS — the flag's sentence, or the API's stored `raw_minutes`
 * where there is no flag (below). Never the punches, so the screen and the
 * approve guard cannot disagree about a shift's length.
 *
 * ── THE FLAG IS NOT THE WHOLE RULE, SO THE SCREEN READS THE API'S VERDICT ──
 * POST /timesheets/:id/approve tests the row's CURRENT length, not the flag. A
 * shift closed before api#992 shipped carries no flag and is still refused with
 * 409 `long_shift_unconfirmed` — TNT's 38.67h shop shift of 2026-10-05 is one.
 *
 * Since api#1010 every timesheet read (GET /timesheets, /timesheets/:id,
 * /timesheets/my/period/:periodId, /timesheets/status and /my/status) carries
 * `is_long_shift`, the API's own length test. That is the ONLY length rule this
 * module knows: the flag's sentence wins for the marker text when present, and
 * a row the API calls long without a flag is marked from `is_long_shift`. A
 * missing or non-true value is not long — the screen never recomputes it
 * (#956's client-side copy of the 16h ceiling is gone). `raw_minutes` is read
 * only to print the figure on such a row, never to decide it.
 */

import { fmtHours } from './display-labels';

export const LONG_SHIFT_FLAG_CODE = 'long_shift';

/** POST /approve refuses a longer note (api#992, LONG_SHIFT_NOTE_MAX). */
export const LONG_SHIFT_NOTE_MAX = 500;

/**
 * The flags as a list. The column is `text[]` and `t.*` serialises it as an
 * array; a bare string is tolerated because this field was typed `string` for
 * years and a cached payload may still hold one. NULL, `[]` and anything else
 * are "no flags" — an empty array is truthy, and treating it as a flag would
 * paint a row red over nothing.
 */
export function anomalyFlagList(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.filter((f): f is string => typeof f === 'string' && f.trim() !== '');
  if (typeof raw === 'string' && raw.trim() !== '') return [raw];
  return [];
}

export function hasAnomalyFlags(raw: unknown): boolean {
  return anomalyFlagList(raw).length > 0;
}

function isLongShiftFlag(flag: string): boolean {
  return flag === LONG_SHIFT_FLAG_CODE || flag.startsWith(`${LONG_SHIFT_FLAG_CODE}:`);
}

/** The hours figure as the API wrote it ("30.15"), or null when it cannot be read. */
function hoursIn(text: string, pattern: RegExp): string | null {
  const m = text.match(pattern);
  return m ? m[1] : null;
}

export interface LongShift {
  /** As written by the API, two places. Null when the sentence carries no figure. */
  hours: string | null;
}

/** The long-shift flag on this row, or null when there is none. */
export function longShiftFromFlags(raw: unknown): LongShift | null {
  const flag = anomalyFlagList(raw).find(isLongShiftFlag);
  if (!flag) return null;
  return { hours: hoursIn(flag, /(\d+(?:\.\d+)?)h\b/) };
}

type LongShiftRow = {
  anomaly_flags?: unknown;
  raw_minutes?: unknown;
  is_long_shift?: unknown;
  isLongShift?: unknown;
};

/** The API's verdict (api#1010). Anything but `true` — absent included — is not long. */
function apiSaysLong(row: LongShiftRow): boolean {
  return (row.isLongShift ?? row.is_long_shift) === true;
}

/**
 * The row's long shift: the flag's when there is one, else the API's
 * `is_long_shift`, else null. On a flagless long row the figure is the API's
 * stored `raw_minutes`, two places as its `shiftHours` rounds — or none.
 */
export function longShiftOf(row: LongShiftRow | null | undefined): LongShift | null {
  if (!row) return null;
  const flagged = longShiftFromFlags(row.anomaly_flags);
  if (flagged) return flagged;
  if (!apiSaysLong(row)) return null;
  const raw = row.raw_minutes;
  const minutes = raw === null || raw === undefined || raw === '' ? NaN : Number(raw);
  // Rounded to two places first, as the API's `shiftHours` does, then the
  // shared formatter — so the figure matches the 409's sentence to the cent.
  return { hours: Number.isFinite(minutes) ? fmtHours(Math.round((minutes / 60) * 100) / 100) : null };
}

/** Flagged for the queue: any anomaly flag, or a long shift by the API's word. */
export function isFlaggedShift(row: LongShiftRow): boolean {
  return hasAnomalyFlags(row.anomaly_flags) || longShiftOf(row) !== null;
}

/** Every flag that is not the long-shift one, verbatim. */
export function otherAnomalyFlags(raw: unknown): string[] {
  return anomalyFlagList(raw).filter((f) => !isLongShiftFlag(f));
}

/** "Long shift: 30.15h — check clock-out". No figure, no invented one. */
export function longShiftLabel(ls: LongShift): string {
  return ls.hours ? `Long shift: ${ls.hours}h — check clock-out` : 'Long shift — check clock-out';
}

/**
 * The 409 POST /approve answers for an unconfirmed long shift, read into the
 * same shape as the flag. The figure comes from the API's message ("This shift
 * is 38.67 hours, …") — a pre-#992 row has no flag to read it from.
 */
export function longShiftRefusal(err: any): LongShift | null {
  const data = err?.response?.data;
  if (err?.response?.status !== 409 || data?.code !== 'long_shift_unconfirmed') return null;
  const msg = typeof data?.error === 'string' ? data.error : '';
  return { hours: hoursIn(msg, /(\d+(?:\.\d+)?) hours/) };
}

/** The body fields that confirm one. Never sent without a non-blank note. */
export interface LongShiftConfirmation {
  confirm_long_shift: true;
  long_shift_note: string;
}
